import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import MGBWChevronLeftIcon from "@tSurya/Icons/MGBWChevronLeftIcon";
import MGBWChevronRightIcon from "@tSurya/Icons/MGBWChevronRightIcon";
import { css } from "styled-components";

import {
    COLORS as SURYA_COLORS,
    COMMON as SURYA_COMMON,
    ELEMENTS as SURYA_ELEMENTS,
    FORM,
    MENU,
    PRODUCT,
    SPACING,
    TYPOGRAPHY as SURYA_TYPOGRAPHY,
} from "@tSurya/theme/design.surya";

// -- Colors

const COLORS = {
    ...SURYA_COLORS,
    primary: {
        base: "#333333",
        contrast: "#FFFFFF",
    },
    secondary: {
        base: "#F9F7F3",
        contrast: "#333333",
    },
    accent: {
        main: "#5E7A80",
        contrast: "#FFFFFF",
    },
    success: "#336633",
    warning: "#BE6A14",
    error: "#AB3326",
    background: {
        ...SURYA_COLORS.background,
        main: "#FFFFFF",
        light: "#FFFFFF",
        lighter: "#FAFAFA",
    },
    border: {
        main: "#E5E5E5",
    },
    disabled: {
        main: "#B3B3B3",
    },
    info: {
        main: "#1C355E",
        contrast: "#FFFFFF",
    },
    // Brand primitive colors
    cream: "#F9F7F3",
    rain: "#5E7A80",
    forest: "#444C3C",
    // Brand accent colors
    rainLegacy: "#4D6063",
    stone: "#B5B2AA",
    khaki: "#A58D6E",
    chestnut: "#66422B",
    // Utility backgrounds
    grayBg: "#F2F2F299",
    // Gray scale
    gray: {
        50: "#FAFAFA",
        100: "#F2F2F2",
        200: "#E5E5E5",
        300: "#CCCCCC",
        400: "#B3B3B3",
        500: "#999999",
        600: "#808080",
        700: "#666666",
        800: "#4D4D4D",
        900: "#333333",
    },
    text: {
        ...SURYA_COLORS.text,
        accent: "#666666",
        disabled: "#999999",
        main: "#333333",
        link: "#4D6063",
        // Figma variable "Text Colors/text-link-hover".
        linkHover: "#466065",
        white: "#FFFFFF",
    },
    input: {
        background: "#FFFFFF",
    },
    filterIcon: {
        primary: {
            base: "#333333",
            contrast: "#FFFFFF",
        },
    },
    heartIcon: {
        color: "#333333",
        fill: "#FFFFFF",
    },
};

// -- Typography

const BODY_TEXT = {
    size: "15px",
    weight: 400,
    fontFamily: "'Manrope', sans-serif",
    lineHeight: "150%",
    letterSpacing: "0.005em",
};

const TYPOGRAPHY = {
    ...SURYA_TYPOGRAPHY,
    fontFamilyImportUrl: "/staticContent/mgbw/fonts/mgbw-fonts.css",
    fontFamily: {
        title: "'Manrope', sans-serif",
        base: "'Manrope', sans-serif",
        heading: "'EB Garamond', serif",
    },
    announcementBar: {
        fontSize: "14px",
        mobileFontSize: "12px",
        fontWeight: 400,
        lineHeight: "21px",
        textTransform: "none" as const,
        letterSpacing: "0.08px",
    },
    breadcrumbs: {
        weight: 400,
        current: {
            weight: 500,
        },
    },
    size: {
        veryTiny: "10px",
        vTiny: "11px",
        tiny: "12px",
        smallest: "13px",
        smaller: "14px",
        small: "15px",
        base: "15px",
        large: "18px",
        larger: "20px",
        largest: "24px",
        xlargest: "26px",
        xxlarge: "28px",
        xxxlarge: "32px",
        xlarge: "30px",
        xxlargest: "46px",
    },
    lineHeight: {
        smaller: 1,
        small: 1.25,
        large: 2,
        ssmall: "15px",
        msmall: "18px",
        lsmall: "20px",
        base: "22px",
        xbase: "24px",
        medium: "28px",
        lmedium: "28px",
        xmedium: "32px",
        xxmedium: "34px",
        xlmedium: "36px",
        larger: "38px",
        largest: "42px",
        xlargest: "46px",
    },
    body: { ...BODY_TEXT },
    p: { ...BODY_TEXT },
    h1: {
        size: 32,
        weight: 500,
        lineHeight: "normal",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        letterSpacing: "0",
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 28px;
                        line-height: normal;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    h2: {
        size: 36,
        weight: 500,
        lineHeight: "normal",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        letterSpacing: "0",
    },
    h3: {
        size: 32,
        weight: 500,
        lineHeight: "normal",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        letterSpacing: "0",
    },
    h4: {
        size: 28,
        weight: 500,
        lineHeight: "normal",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        letterSpacing: "0",
    },
    h5: {
        size: 24,
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        letterSpacing: "0",
    },
    h6: {
        size: 20,
        weight: 500,
        lineHeight: "normal",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        letterSpacing: "0",
    },
    headerPrimary: {
        size: 15,
        fontFamily: "'Manrope', sans-serif",
        color: "text.main",
        transform: "none" as const,
        weight: 400,
        lineHeight: 1.5,
        letterSpacing: "0.10em",
    },
    headerSecondary: {
        size: 15,
        fontFamily: "'Manrope', sans-serif",
        transform: "none" as const,
        weight: 400,
        lineHeight: 1.5,
        letterSpacing: "0.10em",
    },
    headerTertiary: {
        size: 15,
        fontFamily: "'Manrope', sans-serif",
        color: "text.main",
        transform: "uppercase" as const,
        lineHeight: 1.5,
        weight: 500,
        letterSpacing: "0.10em",
    },
    legend: {
        size: 15,
        color: "text.accent",
        transform: "uppercase" as const,
        weight: 800,
    },
    link: {
        size: 15,
        fontFamily: "'Manrope', sans-serif",
        weight: 500,
        lineHeight: "normal",
        letterSpacing: "1.6px",
        textTransform: "uppercase",
        borderWidth: "1px",
    },
    header: {
        size: "15px",
        textTransform: "capitalize",
        letterSpacing: "0.005em",
        lineHeight: "24px",
        weight: 500,
    },
    openTradeAccountLabel: {
        textTransform: "none",
    },
    cartLink: {
        size: "15px",
        textTransform: "capitalize",
        weight: 500,
        letterSpacing: "0.005em",
    },
    signInTitle: {
        size: "15px",
        textTransform: "capitalize",
        weight: 500,
        letterSpacing: "0.005em",
    },
    megamenuAccentLink: {
        weight: 600,
    },
    megaMenuHeading: {
        size: "15px",
        weight: 700,
        letterSpacing: "1.6px",
        lineHeight: "150%",
        textTransform: "uppercase" as const,
        color: COLORS.text.main,
    },
    megaMenuLink: {
        size: "15px",
        weight: 400,
        letterSpacing: "0.08px",
        lineHeight: "150%",
        color: COLORS.text.main,
    },
    footerHeadings: {
        textTransform: "uppercase",
        size: "15px",
        weight: 700,
        fontFamily: "'Manrope', sans-serif",
        fontStyle: "normal",
        lineHeight: "1.5",
        letterSpacing: "1.6px",
    },
    footerLinks: {
        textTransform: "none",
        color: COLORS.text.main,
        letterSpacing: "0.08px",
    },
    footerEndLine: {
        size: "12px",
        weight: 500,
        lineHeight: "1.5",
        fontFamily: "'Manrope', sans-serif",
        fontStyle: "normal",
        letterSpacing: "1px",
        color: COLORS.text.main,
        opacity: 1,
    },
    mobileBrandNavigationLink: {
        size: "12px",
    },
    mobileNavCategoryLinks: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        letterSpacing: "0.10em",
    },
    mobileViewLinksList: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        letterSpacing: "0.10em",
    },
    button: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Manrope', sans-serif",
        letterSpacing: "0.02em",
    },
    pageTitle: {
        size: "32px",
        weight: 500,
        lineHeight: "normal",
        fontStyle: "normal",
        fontFamily: "'EB Garamond', serif",
        textTransform: "none",
        mobileSize: "28px",
        mobileLineHeight: "28px",
    },
    headings: {
        weight: 600,
        fontStyle: "normal",
        fontFamily: "'EB Garamond', serif",
        textTransform: "none",
    },
    widgetTitle: {
        size: "32px",
        lineHeight: "normal",
        weight: 500,
        textTransform: "none",
        fontStyle: "normal",
        fontFamily: "'EB Garamond', serif",
        mobileSize: "28px",
        mobileLineHeight: "normal",
    },
    badge: {
        size: "14px",
        lineHeight: "21px",
        weight: 500,
        fontFamily: "'Manrope', sans-serif",
        textTransform: "uppercase",
        letterSpacing: "1px",
    },
    badgeLarge: {
        size: "15px",
        lineHeight: "24px",
        weight: 800,
        fontFamily: "'Manrope', sans-serif",
        textTransform: "uppercase",
        letterSpacing: "1px",
    },
    heroCarousel: {
        size: "52px",
        lineHeight: "normal",
        fontFamily: "'EB Garamond', serif",
        fontStyle: "normal",
        mobileSize: "28px",
        mobileLineHeight: "28px",
    },
    cardSectionTitle: {
        size: "28px",
    },
    filterButton: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Manrope', sans-serif",
        letterSpacing: "0.02em",
    },
    sortSelectLabel: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Manrope', sans-serif",
        letterSpacing: "0",
    },
    sortSelectInput: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Manrope', sans-serif",
        letterSpacing: "0",
    },
    topCategoryFilter: {
        size: "15px",
        weight: 500,
        fontFamily: "'Manrope', sans-serif",
        lineHeight: "24px",
        letterSpacing: "0",
        color: COLORS.text.main,
    },
    productCard: {
        brand: {
            size: "14px",
            weight: 800,
            fontFamily: "'Manrope', sans-serif",
            lineHeight: "21px",
            letterSpacing: "1px",
        },
        linkTitle: {
            weight: 500,
        },
    },
    accordionItem: {
        size: "15px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Manrope', sans-serif",
        textTransform: "none",
        sizeSmaller: "14px",
    },
    categoryDetailsLinksList: {
        weight: 500,
        size: "15px",
    },
    categoryCard: {
        name: {
            size: "15px",
            weight: 500,
        },
    },
    productDetails: {
        variantButton: {
            // Figma: unselected size button label is Medium (500), selected is Bold (700).
            activeWeight: 700,
            size: "15px",
        },
        inventoryMessage: {
            weight: 600,
        },
        accordionButton: {
            fontFamily: "'Manrope', sans-serif",
            size: "15px",
            weight: 500,
            lineHeight: "1.5",
            textTransform: "uppercase",
            letterSpacing: "1.6px",
            color: COLORS.text.main,
        },
        addToCartButton: {
            size: "14px",
            weight: 800,
            letterSpacing: "0.02em",
            fontFamily: "'Manrope', sans-serif",
            lineHeight: "21px",
            fontWeight: 800,
        },
        priceText: {
            size: 24,
            weight: 600,
        },
    },
};

// -- Elements

const ELEMENTS = {
    ...SURYA_ELEMENTS,
    button: {
        base: {
            padding: "10px 24px",
            borderRadius: "0",
            hover: {
                background: COLORS.background.main,
                borderColor: COLORS.primary.base,
                borderWidth: "1px",
                color: COLORS.text.main,
                boxShadow: "0 1px 2px 0 rgba(10, 13, 18, 0.05)",
            },
            disabled: {
                background: COLORS.gray["300"],
                borderColor: "transparent",
                color: COLORS.gray["800"],
                boxShadow: "0 1px 2px 0 rgba(10, 13, 18, 0.05)",
            },
        },
        secondary: {
            background: COLORS.background.main,
            borderColor: COLORS.primary.base,
            borderWidth: "1px",
            color: COLORS.text.main,
            boxShadow: "0 1px 2px 0 rgba(10, 13, 18, 0.05)",
            hover: {
                background: COLORS.primary.base,
                borderColor: COLORS.primary.base,
                color: COLORS.primary.contrast,
            },
        },
        link: {
            letterSpacing: "1.6px",
        },
    },
    ctaLink: {
        color: "#333333",
    },
    radio: {
        size: "20px",
        dotSize: "10px",
        dotOffset: "5px",
        borderColor: COLORS.primary.base,
        checkedBackground: COLORS.primary.base,
    },
    selectIconSize: "14px",
    cartLink: {
        height: "unset",
    },
    logo: {
        maxWidth: "200px",
    },
    navigationItemDesktop: {
        padding: "15px 0",
    },
    megamenu: {
        boxShadow: "0 5px 12px 0 rgba(0, 0, 0, 0.10)",
        padding: "32px 210px",
        gridPadding: "0",
        columnGap: "0",
        justifyContent: "space-between",
    },
    shopByCollection: {
        heading: "SHOP BY COLLECTION",
        gap: "12px",
        rowGap: "12px",
        justifyContent: "space-between",
        tileWidth: "90px",
        imageSize: "90px",
        captionMarginTop: "4px",
    },
    menuItem: {
        marginBottom: "8px",
        marginTop: "12px",
    },
    carouselArrows: {
        size: "36px",
        boxShadow: "none",
        border: "1px solid #E5E5E5",
        padding: "20px",
        iconTop: "0",
    },
    footerLinks: {
        marginBottom: "12px",
    },
    modal: {
        closeButton: {
            transparent: true,
        },
    },
    formField: {
        borderRadius: "0",
        inputHeight: "48px",
        inputPadding: "12px 20px 12px 20px",
        borderColor: COLORS.gray["300"],
        label: {
            fontFamily: TYPOGRAPHY.fontFamily.base,
            fontWeight: "500",
            fontSize: "15px",
            lineHeight: "1.5",
            color: COLORS.text.main,
        },
        hint: {
            fontSize: "14px",
            fontWeight: "400",
            color: COLORS.text.main,
        },
        error: {
            fontSize: "14px",
            fontWeight: "400",
            color: COLORS.error,
        },
    },
    filterButton: {
        padding: "8px 16px",
        border: "1px solid #E5E5E5",
        borderRadius: "0",
        height: "40px",
        borderColor: "#E5E5E5",
    },
    sortSelect: {
        height: "40px",
        marginRight: "0",
    },
    accordionContainer: {
        desktop: {
            paddingLeft: "0",
            marginTop: "20px",
        },
        mobile: {
            paddingLeft: "20px",
        },
    },
    accordionItem: {
        borderBottom: "1px solid #E5E5E5 !important",
    },
    filterCheckbox: {
        checkedBackground: "#333333",
    },
    cardContainerStyles: {
        cardDivider: {
            width: "100%",
        },
    },
    categoryDetailsLinksList: {
        paddingLeft: "0",
    },
    gridViewClickable: {
        marginRight: "0",
    },
    cardContainerMultiColumnGridItem: {
        padding: "10px",
        paddingLeft: "10px",
    },
    cardList: {
        gridContainerOffset: {
            marginLeft: "-10px",
            marginRight: "-10px",
            width: "calc(100% + 20px)",
            mobile: {
                marginLeft: "0",
                marginRight: "0",
                width: "100%",
            },
            // Swatches PLP on mobile shows two cards per row with 8px GridItem padding (16px gap).
            // The page <main> has 15px side padding, so the GridOffset must pull outward by 7px
            // to make the edge padding match the inter-card gap: 15 - 7 + 8 = 16px.
            swatchMobile: {
                marginLeft: "-7px",
                marginRight: "-7px",
                width: "calc(100% + 14px)",
            },
        },
    },
    productListing: {
        heading: {
            marginBottom: "0",
            marginTop: "0",
            paddingTop: "2px",
            textAlign: "center",
        },
        totalSortContainer: {
            marginTop: "0",
        },
    },
    productDetails: {
        variantButton: {
            // Figma 3407:24158 "Buttons": px-20 py-8, h 40, white bg, #ccc border (#333 when selected).
            padding: "8px 20px",
            borderRadius: "0",
            borderColor: "#CCCCCC",
            activeBorder: "1px solid #333333",
            activeBackground: "#FFFFFF",
            buttonMarginBottom: "0",
        },
        // Figma 3407:24156 "Size" label: CTA 14px/Bold, ls 1.6px, uppercase, 12px gap to buttons.
        variantButtonLabel: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "1.5",
            letterSpacing: "1.6px",
            textTransform: "uppercase",
            marginBottom: "12px",
        },
        accordionSection: {
            marginBottom: "0",
            marginBottomMobile: "0",
        },
        accordionButton: {
            backgroundColor: "#FFFFFF",
            // Figma 3303:17381: section is padded 16px top/bottom with a 12px gap to the panel.
            padding: "16px 0",
            expandedPadding: "16px 0 12px",
            expandedBorderBottom: "1px solid transparent",
            borderTop: "1px solid #CCCCCC",
            borderBottom: "none",
            borderLeft: "none",
            borderRight: "none",
        },
        // Figma 3314:35916 "Details Expanded": PDP accordion caret is a thin 20px chevron
        // (down when collapsed, up when expanded) instead of the shared theme Plus/Minus.
        accordionToggleIcon: {
            size: 20,
        },
        // Figma 3314:29990 "Resources": resource rows use the 16px file icon.
        accordionResourceIcon: {
            size: 16,
        },
        accordionResourceContainer: {
            gap: "12px",
        },
        accordionResourceLink: {
            fontSize: "15px",
            fontWeight: 600,
            lineHeight: "1.5",
            letterSpacing: "0.08px",
            color: COLORS.accent.main,
            hoverColor: COLORS.text.linkHover,
        },
        accordionPanel: {
            paddingBottom: "16px",
        },
        accordionPanelUnorderedList: {
            padding: "0 0 0 22.5px",
            paddingTop: "0",
            letterSpacing: "0.08px",
        },
        accordionDocumentsWrapper: {
            padding: "0",
        },
        customproducttilewrapper: {
            padding: "0",
        },
        addToCartButton: {
            width: "100%",
            height: "48px",
        },
        // Swatch PDP: sections are spaced by FlexRow zone gaps (20px), so the widgets themselves
        // must not add their own outer margins on MGBW. Consumed with fallbacks - other brands
        // keep their original hardcoded values.
        variantOptions: {
            marginTop: "0",
        },
        variantButtonWrapper: {
            marginTop: "0",
            marginBottom: "0",
            mobileMarginTop: "0",
        },
        qtySelector: {
            marginBottom: "0",
            labelPaddingRight: "0",
        },
        addToProjectButton: {
            width: "100%",
            iconSize: 18,
        },
        msrpWrapper: {
            margin: "0",
        },
        msrpContainer: {
            color: "#666666",
            size: "13px",
            marginRight: "2px",
        },
        productPriceWrapper: {
            marginRight: "24px",
            marginTop: "0",
            marginBottom: "0",
        },
        // Figma 3407:24163: 16px between the price row and the qty + add-to-cart row.
        priceBlockMarginBottom: "16px",
        // Case-goods PDP romance copy. Figma 3314:35874: Body/14px/Regular, text-primary, 12px above.
        dataPointContent: {
            size: "14px",
            weight: 400,
            letterSpacing: "0.08px",
            color: "#333333",
        },
        productDataPoint: {
            titleGridItem: {
                size: "32px",
                margin: "8px 0",
            },
            productName: {
                size: "32px",
                margin: "8px 0",
                weight: 600,
                lineHeight: "1.5",
            },
            // Case-goods PDP title stack (Figma 3415:28541): collection name over color/finish, 4px gap,
            // 12px to the romance copy below. Same type ramp as WIDGETS.swatchPdpTitle.
            mgbwTitleStack: {
                collectionName: {
                    fontFamily: TYPOGRAPHY.fontFamily.heading,
                    fontSize: "28px",
                    mobileFontSize: "24px",
                    fontWeight: 500,
                    lineHeight: "1.3214",
                    letterSpacing: "0",
                    color: COLORS.text.main,
                    marginBottom: "4px",
                },
                colorFinish: {
                    fontFamily: TYPOGRAPHY.fontFamily.base,
                    fontSize: "20px",
                    mobileFontSize: "18px",
                    fontWeight: 500,
                    lineHeight: "1.5",
                    letterSpacing: "0.08px",
                    color: COLORS.text.main,
                    marginBottom: "12px",
                },
            },
        },
        mobileViewAddToCartMobileDiv: {
            display: "block",
        },
        productAvailabilityQuantity: {
            desktop: {
                marginTop: "12px",
            },
            mobile: {
                marginTop: "8px",
            },
        },
        topGridItem: {
            desktop: {
                padding: "15px 20px 0 0",
                marginTop: "0",
            },
            mobile: {
                padding: "0",
                marginTop: "0",
            },
        },
        rightColumnGridItem: {
            desktop: {
                paddingLeft: "20px",
            },
            mobile: {
                paddingLeft: "0",
            },
        },
        priceContainer: {
            marginTop: "15px",
            marginBottom: "15px",
        },
        productImageGallery: {
            wrapperMaxHeight: "656px",
            mainWrapperBg: "transparent",
            thumbBg: "transparent",
        },
    },
    breadcrumbs: {
        padding: "12px 0",
        // MGBW-only: hide the breadcrumb on category PLPs that render SurSubcategoryNav.
        // These are matched by the current category's urlSegment (same value used in the
        // SurSubcategoryNav "Category Filter" CMS field). Search-results PLPs have no
        // category so they are unaffected and keep their wrapper/padding.
        // Add more segments here if other nav landing pages are configured (e.g. "living").
        hideOnCategorySegments: ["all"],
    },
    productImage: {
        padding: "0",
    },
    pagination: {
        padding: "0",
        size: "15px",
        minHeight: "unset",
        hideFirstLast: "true",
        gap: "16px",
        containerGap: "32px",
        pageColor: "#333333",
        activeBackground: "#333333",
        activeColor: "white",
        activeSize: "32px",
        activeBorderRadius: "1px",
        buttonBorder: "none",
        buttonBackground: "transparent",
        arrowSize: "20px",
        labelFontWeight: "500",
        arrowDisabledOpacity: "1",
        paginationWindow: "5",
    },
    categoryDetailBanner: {
        textAlign: "center",
        color: "#333333",
        maxWidth: "980px",
        size: "15px",
        letterSpacing: "0",
        lineHeight: "1.5",
        margin: "8px auto 0",
    },
    categoryListing: {
        mainContentContainer: {
            marginTop: "32px",
        },
        pageTitle: {
            marginBottom: "0",
        },
    },
};

// -- Widgets

const WIDGETS = {
    myListsDetails: {
        quickAddMobileLeftPadding: "5px",
        quickAddCloseIconMobileMarginTop: "27.5px",
    },
    mgbwRelatedProducts: {
        section: {
            gap: "32px",
            mobilePadding: "0px 0",
            mobileMarginTop: "-40px",
            desktopPadding: "80px 0px",
            desktopContentMaxWidth: "1360px",
            desktopOuterGutter: "80px",
            background: COLORS.background.main,
        },
        header: {
            gap: "12px",
            mobilePadding: "0 16px",
        },
        mobile: {
            carouselMaxWidth: "767px",
            cardWidth: "240px",
            cardGap: "12px",
            arrowGap: "16px",
            arrowSize: "20px",
        },
        desktop: {
            columns: 4,
            columnGap: "32px",
            rowGap: "40px",
            centeredGridWidths: {
                1: "calc(25% - 24px)",
                2: "calc(50% - 16px)",
                3: "calc(75% - 8px)",
            },
        },
        image: {
            aspectRatio: "2000 / 1933",
            background: COLORS.background.main,
        },
        productCard: {
            gap: "12px",
            fontFamily: TYPOGRAPHY.fontFamily.base,
            fontWeight: 500,
            fontSize: "15px",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
            color: COLORS.primary.base,
        },
        viewAll: {
            borderWidth: "1px",
            padding: "4px 0",
            fontFamily: TYPOGRAPHY.fontFamily.base,
            fontWeight: 500,
            fontSize: "15px",
            lineHeight: "1.5",
            letterSpacing: "1.6px",
            color: COLORS.primary.base,
        },
    },
    promotionSummary: {
        fontFamily: "'Mulish', sans-serif",
        discountFontSize: "14px",
        discountValueFontSize: "15px",
        labelFontSize: "15px",
        valueFontSize: "15px",
        messageFontSize: "13px",
        messageWhiteSpace: "nowrap",
        savingsColor: COLORS.success,
        messageColor: "#777777",
        calloutBackground: "#EFF8F1",
        calloutBorder: "#DCEFE0",
        calloutTextColor: COLORS.success,
        calloutMarginTop: "4px",
        calloutGap: "8px",
        calloutPadding: "10px 12px",
        calloutIconSize: "16px",
        calloutFontSize: "13px",
        calloutLineHeight: "1.4",
        dividerColor: "#E0E0E0",
        dividerMargin: "4px 0",
        estimatedTotalFontSize: "15px",
        estimatedTotalFontWeight: 700,
        estimatedTotalLineHeight: "1.4",
        estimatedTotalHelperFontSize: "13px",
        estimatedTotalHelperLineHeight: "1.4",
        estimatedTotalHelperMarginTop: "2px",
    },
    checkoutNavigation: {
        iconWidth: "21px",
        iconHeight: "8px",
        iconGap: "8px",
        backButtonColor: COLORS.text.link,
        desktopBackMarginTop: "30px",
    },
    checkoutPaymentInformation: {
        hostedPaymentField: {
            height: "48px",
            inlineEndInset: "1px",
            padding: "12px 10px",
            stateBorderWidth: "1px",
        },
        summaryAllocation: {
            borderTop: `1px solid ${COLORS.border.main}`,
            margin: "0 20px",
            padding: "12px 0 16px",
            rowGap: "8px",
            labelAmountGap: "12px",
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "1.4",
            dueTodayColor: COLORS.success,
            onTermsColor: COLORS.warning,
        },
        paymentBreakdown: {
            optionGap: "12px",
            optionBorder: `1px solid ${COLORS.border.main}`,
            optionSelectedBorder: `1px solid ${COLORS.accent.main}`,
            optionSelectedBoxShadow: `0 0 0 1px ${COLORS.accent.main}`,
            boxBorderRadius: "6px",
            optionHeaderPadding: "16px 20px 16px 28px",
            optionHeaderMobilePadding: "14px 12px 14px 20px",
            optionLabelGap: "16px",
            optionTextMinWidth: "180px",
            optionTitleFontWeight: 700,
            optionTitleLineHeight: "1.3",
            optionSubtitleFontSize: "12px",
            optionSubtitleLineHeight: "1.3",
            optionAmountFontSize: "18px",
            optionAmountMobileFontSize: "15px",
            optionAmountFontWeight: 700,
            optionAmountLineHeight: "1.3",
            selectedContentGap: "8px",
            selectedContentPadding: "0 20px 20px 40px",
            selectedContentMobilePadding: "0 12px 16px 16px",
            chargedTodayColor: COLORS.success,
            onTermsColor: COLORS.warning,
            compactRowGap: "16px",
            compactRowPadding: "12px 16px",
            compactRowMobilePadding: "12px",
            compactRowBackground: COLORS.checkout.lightBg,
            compactRowAccentWidth: "4px",
            compactMetaFontSize: "11px",
            compactMetaFontWeight: 700,
            compactMetaLetterSpacing: "0.06em",
            compactMetaLineHeight: "1.3",
            compactLabelMargin: "2px 0 0",
            compactLabelFontSize: "12px",
            compactLabelLineHeight: "1.3",
            compactAmountFontSize: "16px",
            compactAmountFontWeight: 700,
            compactAmountLineHeight: "1.3",
            disclosureMinHeight: "44px",
            disclosurePadding: "8px 0",
            disclosureColor: COLORS.text.link,
            disclosureFontSize: "13px",
            disclosureFontWeight: 700,
            disclosureLineHeight: "1.4",
            disclosureUnderlineOffset: "3px",
            disclosureFocusOutline: `2px solid ${COLORS.accent.main}`,
            disclosureFocusOutlineOffset: "2px",
            expandedContentPaddingTop: "4px",
            detailSectionGap: "12px",
            detailSectionMinWidth: "240px",
            detailBorder: `1px solid ${COLORS.border.main}`,
            detailHeaderGap: "12px",
            detailHeaderPadding: "10px 12px",
            detailHeaderBackground: COLORS.checkout.lightBg,
            detailHeaderAccentWidth: "4px",
            detailHeaderFontWeight: 700,
            detailTagFontSize: "11px",
            detailTagFontWeight: 700,
            detailTagLetterSpacing: "0.06em",
            detailBodyPadding: "12px",
            detailLabelFontSize: "11px",
            detailLabelLetterSpacing: "0.06em",
            detailLabelMarginBottom: "10px",
            detailRowGap: "12px",
            detailRowMarginBottom: "6px",
            detailTotalPaddingTop: "12px",
            detailTotalBorder: `1px solid ${COLORS.border.main}`,
            detailTotalFontWeight: 700,
            finalTotalGap: "12px",
            finalTotalMarginTop: "12px",
            finalTotalPaddingTop: "12px",
            finalTotalBorder: `1px solid ${COLORS.border.main}`,
            finalTotalFontWeight: 700,
            finalTotalAmountFontSize: "18px",
            additionalMessagePaddingTop: "12px",
            additionalMessageBorder: `1px solid ${COLORS.border.main}`,
            additionalMessageFontSize: "12px",
        },
        termsMethod: {
            background: COLORS.checkout.lightBg,
            border: `1px solid ${COLORS.border.main}`,
            borderRadius: "4px",
            gap: "4px",
            padding: "14px 16px",
        },
        termsMethodLabel: {
            color: COLORS.checkout.text.secondary,
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "1.3",
        },
        termsMethodValue: {
            color: COLORS.checkout.text.primary,
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "1.3",
        },
        termsMethodHelper: {
            color: COLORS.checkout.text.secondary,
            fontSize: "12px",
            lineHeight: "1.3",
        },
    },
    announcementBar: {
        defaultBackground: COLORS.forest,
        defaultTextColor: "#FFFFFF",
    },
    configuratorOrderSwatch: {
        marginTop: "4px",
        color: COLORS.accent.main,
        fontFamily: TYPOGRAPHY.fontFamily.base,
        fontSize: "15px",
        fontWeight: 500,
        lineHeight: "1.5",
        letterSpacing: "0.08px",
        underlinePaddingBottom: "2px",
    },
    productDetails: {
        availability: {
            closeButton: {
                size: "36px",
                padding: "4px",
                iconSize: "20px",
            },
            status: {
                color: COLORS.forest,
                fontFamily: TYPOGRAPHY.fontFamily.base,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "1.5",
                letterSpacing: "0.08px",
            },
            link: {
                color: COLORS.accent.main,
                fontFamily: TYPOGRAPHY.fontFamily.base,
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "1.5",
                letterSpacing: "0.08px",
                borderBottom: `1px dashed ${COLORS.accent.main}`,
            },
        },
        crossSellLink: {
            color: COLORS.accent.main,
            fontFamily: TYPOGRAPHY.fontFamily.base,
            fontSize: "15px",
            fontWeight: 500,
            lineHeight: "1.5",
            letterSpacing: "0.08px",
            underlinePaddingBottom: "2px",
        },
        anchors: {
            gap: "16px",
            padding: "24px 0",
            fontFamily: TYPOGRAPHY.fontFamily.base,
            fontSize: "15px",
            lineHeight: "1.5",
            letterSpacing: "1.6px",
            linkColor: COLORS.accent.main,
            linkFontWeight: 700,
            separatorColor: COLORS.gray[300],
            separatorFontWeight: 500,
        },
    },
    productColors: {
        // Figma: up to `itemsPerView` swatches are shown in a row; more than that becomes a carousel.
        itemsPerView: 6,
        itemGap: "12px",
        // Max rendered swatch size; swatches shrink to fit the per-view slot on narrower columns.
        swatchSize: "68px",
    },
    // Swatch PDP title area: replaces the standard Page Title on the swatches PDP variant with two
    // stacked lines - the collection name (uxCollectionName) and the color name (uxColorName).
    // Figma: PDP - Swatch, frame 3693:9035 (Headlines/H4 28px/Medium + Body/20px/Medium), 4px gap.
    swatchPdpTitle: {
        collectionName: {
            fontFamily: TYPOGRAPHY.fontFamily.heading,
            fontSize: "28px",
            mobileFontSize: "24px",
            fontWeight: 500,
            // Figma renders the 28px line in a 37px box. Browser "normal" gives ~36px for EB Garamond,
            // so use the exact ratio 37/28 (unitless, scales with the mobile font size too).
            lineHeight: "1.3214",
            letterSpacing: "0",
            color: COLORS.text.main,
            marginBottom: "4px",
        },
        colorName: {
            fontFamily: TYPOGRAPHY.fontFamily.base,
            fontSize: "20px",
            mobileFontSize: "18px",
            fontWeight: 500,
            lineHeight: "1.5",
            letterSpacing: "0.08px",
            color: COLORS.text.main,
            // Figma 3407:24123: title block -> description gap is 12px.
            marginBottom: "12px",
        },
    },
    // Swatch PDP "Item# XXXX" line. Figma 3407:24161: Body/14px/Regular, text-secondary (#666).
    swatchPdpItemNumber: {
        fontFamily: TYPOGRAPHY.fontFamily.base,
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.5",
        letterSpacing: "0.08px",
        color: "#666666",
    },
    imageGrid: {
        imageWidth: [12, 12, 12, 7, 7] as const,
        contentWidth: [12, 12, 12, 5, 5] as const,
        imageMaxWidth: "670px",
        contentMaxWidth: "550px",
        desktopImageFlexBasis: "670px",
        desktopContentFlexBasis: "550px",
        desktopGap: "60px",
        desktopNoWrap: true,
        containerMaxWidth: "none",
        containerMargin: "0",
        containerMarginBottom: "0",
        imageFlexBasis: "60%",
        contentFlexBasis: "40%",
        gridJustifyContent: "center",
        padding: {
            left: "40px",
            right: "40px",
        },
        verticalPadding: "80px 40px",
        mobileXPadding: "16px",
        mobilePadding: "32px 16px",
        mobileLastChildMarginBottom: "0",
        mobileContentPadding: "0",
        mobileContentGap: "16px",
        image: {
            aspectRatio: "1.56",
            mobileAspectRatio: "358/240",
        },
        contentTitle: {
            variant: "h2" as const,
            fontSize: "32px",
            responsiveFontSizes: ["28px", "28px", "28px", null, null],
            marginBottom: "20px",
            mobileMarginBottom: "12px",
            fontWeight: "500",
            lineHeight: "normal",
        },
        contentDesc: {
            marginBottom: "24px",
            mobileMarginBottom: "0",
            fontWeight: "400",
            letterSpacing: "0.08px",
        },
        linksContainer: {
            marginTop: "8px",
            mobileMarginTop: "4px",
            compactBreakpoint: "380px",
            compactGap: "8px",
            compactFontSize: "12px",
        },
        preTitle: {
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            fontSize: "24px",
            fontWeight: "400",
            lineHeight: "normal",
            letterSpacing: "0.019px",
            color: "#444C3C",
            mobileFontSize: "20px",
        },
        linkWrapper: {
            marginBottom: "32px",
        },
    },
    featuredCategory: {
        slideTitle: {
            weight: 500,
            textTransform: "none",
        },
    },
    featuredShowcase: {
        padding: "60px 40px",
        mobilePadding: "32px 16px",
        gap: "32px",
        mobileGap: "0",
        preTitle: {
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            fontSize: "24px",
            fontWeight: "400",
            lineHeight: "150%",
            letterSpacing: "0.8px",
            color: "#444C3C",
            marginBottom: "4px",
            mobileMarginBottom: "0",
            mobileFontSize: "20px",
        },
        title: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "32px",
            fontWeight: "500",
            lineHeight: "normal",
            mobileFontSize: "28px",
        },
        description: {
            fontSize: "15px",
            lineHeight: "1.5",
            marginBottom: "12px",
            desktopPaddingTop: "12px",
            mobileMarginTop: "12px",
            mobileMarginBottom: "0",
            mobileFontSize: "15px",
            mobileLetterSpacing: "0.08px",
        },
        mobileCtaArrowsRow: {
            marginTop: "20px",
            marginBottom: "32px",
        },
        cta: {
            marginTop: "8px",
        },
        grid: {
            columns: 4,
            gap: "26px",
        },
        image: {
            objectFit: "contain",
            aspectRatio: "1/1",
        },
        slideTitle: {
            fontFamily: "'Manrope', sans-serif",
            weight: "500",
            fontSize: "15px",
            lineHeight: "150%",
            letterSpacing: "0.08px",
            marginTop: "12px",
            textTransform: "none",
        },
    },
    collectionShowcase: {
        padding: "80px 0",
        mobilePadding: "32px 0",
        gap: "32px",
        header: {
            gap: "16px",
            maxWidth: "700px",
            mobileSidePadding: "16px",
        },
        title: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "32px",
            fontWeight: "500",
            lineHeight: "normal",
            mobileFontSize: "28px",
        },
        description: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "15px",
            fontWeight: "400",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
        },
        images: {
            gap: "20px",
        },
        image1: {
            width: "61.11%",
            aspectRatio: "880/660",
            objectFit: "cover",
            mobileAspectRatio: "390/350",
        },
        image2: {
            objectFit: "cover",
            mobileAspectRatio: "390/300",
        },
    },
    flexibleContent: {
        padding: "100px 40px",
        mobilePadding: "32px 16px",
        gap: "60px",
        mobileGap: "32px",
        imagesGap: "20px",
        mobileImagesGap: "12px",
        imageHeight: "400px",
        mobileImageHeight: "300px",
        textWidth: "500px",
        preTitle: {
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            fontSize: "24px",
            fontWeight: "400",
            lineHeight: "1.5",
            letterSpacing: "0.8px",
            color: "#444C3C",
            mobileFontSize: "20px",
        },
        title: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "32px",
            fontWeight: "500",
            lineHeight: "normal",
            mobileFontSize: "28px",
        },
        description: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "15px",
            fontWeight: "400",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
        },
        cta: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "15px",
            fontWeight: "500",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
            height: "44px",
            padding: "10px 24px",
            borderColor: "#333",
            color: "#333",
            backgroundColor: "#fff",
        },
        textBlockGap: "16px",
        titlesGap: "4px",
        contentToCtaGap: "32px",
        mobileContentToCtaGap: "20px",
    },
    brandPillars: {
        padding: "80px 40px",
        mobilePadding: "32px 16px",
        columnsGap: "60px",
        mobileColumnsGap: "40px",
        maxWidth: "1360px",
        pillarGap: "20px",
        iconCircle: {
            background: "#EFEAE0",
            iconSize: "64px",
        },
        textGap: "8px",
        title: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "28px",
            fontWeight: "500",
            lineHeight: "normal",
        },
        description: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "22.5px",
            letterSpacing: "0.08px",
        },
    },
    categoryCarousel: {
        padding: "80px 40px",
        mobilePadding: "32px 0",
        gap: "32px",
        mobileGap: "32px",
        headerGap: "32px",
        mobileSidePadding: "16px",
        preTitle: {
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            fontSize: "24px",
            fontWeight: "400",
            lineHeight: "normal",
            letterSpacing: "0.8px",
            color: "#444C3C",
            mobileFontSize: "20px",
        },
        title: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "32px",
            fontWeight: "500",
            lineHeight: "normal",
            mobileFontSize: "28px",
        },
        arrows: {
            padding: "10px",
            mobileGap: "16px",
        },
        slide: {
            width: "500px",
            height: "620px",
            gap: "20px",
            mobileWidth: "280px",
            mobileHeight: "347px",
            mobileGap: "12px",
        },
        label: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "24px",
            fontWeight: "600",
            letterSpacing: "0.384px",
            color: "white",
            bottom: "46px",
            textTransform: "uppercase",
        },
    },
    heroCarousel: {
        slideTitle: {
            size: "64px",
            lineHeight: "64px",
        },
    },
    heroBanner: {
        overlayPadding: "0",
    },
    tabGroup: {
        wrapper: {
            padding: "4px 0",
        },
    },
    productListDrawer: {
        size: undefined,
        width: "100vw",
        top: "0",
        title: {
            letterSpacing: "1px",
        },
    },
    subcategoryNav: {
        paddingTop: "40px",
        paddingBottom: "32px",
        paddingHorizontal: "0",
        mobilePaddingTop: "32px",
        mobilePaddingBottom: "20px",
        mobilePaddingHorizontal: "0",
        gap: "24px",
        imageSize: "172px",
        mobileImageSize: "100px",
        itemGap: "4px",
        label: {
            fontFamily: TYPOGRAPHY.fontFamily.title,
            fontSize: "15px",
            fontWeight: "500",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
        },
        mobileSlideWidth: "110px",
        mobileSlideMargin: "12px",
    },
    swatchGrid: {
        padding: "80px 40px",
        mobilePadding: "32px 16px",
        gap: "32px",
        mobileGap: "21px",
        preTitle: {
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            fontSize: "24px",
            fontWeight: "400",
            lineHeight: "normal",
            letterSpacing: "0.0192px",
            color: "#444C3C",
            mobileFontSize: "20px",
            mobileLetterSpacing: "0.016px",
        },
        title: {
            fontFamily: "'EB Garamond', serif",
            fontSize: "32px",
            fontWeight: "500",
            lineHeight: "normal",
            mobileFontSize: "28px",
        },
        titlesGap: "4px",
        grid: {
            gap: "32px",
            mobileGap: "16px",
            desktopCardWidth: "300px",
        },
        card: {
            gap: "12px",
        },
        image: {
            aspectRatio: "1 / 1",
            objectFit: "cover",
        },
        label: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "15px",
            fontWeight: "500",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
        },
        carousel: {
            slideWidth: "200px",
            slideMargin: "20px",
            overflowExtend: "16px",
        },
        cta: {
            fontFamily: "'Manrope', sans-serif",
            fontSize: "15px",
            fontWeight: "500",
            lineHeight: "1.5",
            letterSpacing: "0.08px",
            height: "44px",
            padding: "10px 24px",
        },
    },
};

// -- Common

const COMMON = {
    ...SURYA_COMMON,
    breakpoints: [0, 576, 768, 992, 1440],
    width: {
        ...SURYA_COMMON.width,
        content: 1360,
    },
    layout: {
        mobileSpacing: "32px",
        tabletSpacing: "32px",
        desktopSpacing: "80px",
    },
};

// -- Z-Index

const Z_INDEX = {
    popover: 1260,
    datePicker: 1270,
    dynamicDropdown: 1280,
    stickyFooter: 1284,
    loadingOverlay: 1285,
    menu: 1290,
    drawer: 1300,
    modal: 1300,
    tabGroup: 400,
    toaster: 1400,
    shellPopover: 1310,
};

// -- Components

const COMPONENTS = {
    pagination: {
        defaultProps: {
            navIconsSrc: {
                firstPage: MGBWChevronLeftIcon,
                previousPage: MGBWChevronLeftIcon,
                nextPage: MGBWChevronRightIcon,
                lastPage: MGBWChevronRightIcon,
            },
        },
    },
};

export { COLORS, TYPOGRAPHY, SPACING, PRODUCT, COMMON, MENU, FORM, ELEMENTS, WIDGETS, Z_INDEX, COMPONENTS };
